import {
  DataTable,
  DataTableProps,
  defaultSidebar,
  multiRowSelectionCheckboxOnly
} from "@appretio/ui-kit";
import type {
  CellValueChangedEvent,
  ColumnState,
  GetRowIdParams,
  GridApi,
  RowClassParams,
  RowDataUpdatedEvent
} from "ag-grid-enterprise";
import type { AgGridReact } from "ag-grid-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import type { ForecastMetaDataType } from "../../hooks/useFetchForecastMetaData";
import type { GridStateRetention } from "../../hooks/useForecastWorkspace/useGridStateRetention";
import {
  CALENDAR_VIEW_TO_TYPE,
  partitionViewOwnedFilters,
  type ResolvedViewState
} from "../../savedViews/resolveViewRows";
import type { ForecastTableControlValues } from "../tableControls/forecastTableControlValues";
import {
  ColumnType,
  createColumnDefinitions
} from "./columnDefinitions/columnFactory";
import type { CubeWindow } from "./utils/columnUtils";
import { toAbsoluteCellChange } from "./utils/indexUtils";
import { forecastRowId } from "./utils/rowId";
import { getRowStyle } from "./utils/rowUtils";

const TIME_PERIOD_TO_COLUMN_TYPE: Record<
  ForecastTableControlValues["timePeriod"],
  ColumnType
> = {
  WK: "weekly",
  MO: "monthly",
  QTR: "quarterly",
  HY: "half-yearly",
  PTG: "PTG"
};

// Stable identity for callers with no view layer — a fresh object per
// render would re-apply the model on every render.
const NO_VIEW_STATE: ResolvedViewState = { filterModel: null, sortModel: [] };

/** Props for the forecast data table, extending the base DataTable with forecast-specific controls. */
/** Props for the ForecastDataTable component. */
export interface ForecastDataTableProps extends Omit<
  DataTableProps,
  | "columnDefs"
  | "selectionColumnDef"
  | "rowSelection"
  | "columnHoverHighlight"
  | "rowHeight"
  | "statusBar"
  | "alwaysMultiSort"
  | "getRowStyle"
  | "getRowId"
  | "loading"
  | "onGridReady"
  | "onStateUpdated"
  | "initialState"
> {
  /** Table control values that drive column generation and filtering. */
  tableControls: Pick<
    ForecastTableControlValues,
    | "calendarView"
    | "weekLabels"
    | "timePeriod"
    | "planBy"
    | "planningYear"
    | "gridStyleGrouping"
    | "intake"
  >;
  /**
   * Filter + sort state the applied view resolves to — the grid's single
   * source for both. Callers memoize it (``useViewState`` for the pages,
   * the range-plan modal for its own view strip); a new identity replaces
   * the model wholesale, which is what a view switch means. Omit it for a
   * grid with no view layer: every row, no sort.
   */
  viewState?: ResolvedViewState;
  /**
   * Producer type whose rows are editable. Callers must pass this
   * explicitly — forecast pages pass ``"user"``, budget pages pass
   * ``"budget"`` (or ``undefined`` when frozen to disable edits).
   *
   * Decision N widens the type to ``string`` so the range-plan modal
   * can pass its own producer label (``"user"`` for the planner's
   * edits, with ``"reference"`` rows staying read-only). Forecast /
   * budget continue to pass the same literals as before.
   */
  editableProducer: string | undefined;
  /**
   * Decision N: optional per-row editability narrowing. When supplied,
   * a cell is editable only if the existing metric/producer/window
   * rules pass AND this predicate returns true for the row (e.g. the
   * range-plan modal restricts edits to ``metric_name === "NIS3"``).
   * Forecast's existing caller leaves this undefined, so behaviour is
   * unchanged for forecast / budget.
   */
  editableRowPredicate?: (row: { metric_name: string }) => boolean;
  /**
   * Decision N: optional metric-editability whitelist. Replaces the
   * cube's own ``metricEditableMapping`` (a forecast-page assumption
   * that NIS3 is read-only because forecasts are planned by GS1 /
   * intake). Range plan passes ``new Set(["NIS3"])`` to flip the
   * default and let planners edit per-week NIS3 directly. Forecast /
   * budget leave this undefined → behaviour unchanged.
   */
  editableMetricsOverride?: ReadonlySet<string>;
  /**
   * Decision N: optional per-cell time-window override. Replaces the
   * forecast-page default "current week only" editability gate with
   * a caller-supplied rule (e.g. range-plan's
   * ``year_week >= week_from``). The other gates (metric / producer /
   * row predicate / intake / cutoff) still apply on top. Forecast's
   * existing callers leave this undefined → behaviour unchanged.
   */
  editableTimeWindowPredicate?: (cell: {
    metric_name: string;
    producer_type: string;
    fiscal_year_name: string;
    colId: string;
  }) => boolean;
  /**
   * Decision N: optional per-column resolver that maps each weekly
   * column id (``"W##"``) to the YYYYWW it represents. When set, the
   * cube's weekly headerTooltip embeds the Mon→Sun date range + the
   * fiscal-year-pair tag so the tooltip is identical to the main
   * range-plan grid's. Forecast / budget callers leave undefined.
   */
  headerTooltipYearWeekResolver?: (
    calendarWeekKey: string
  ) => number | undefined;
  /**
   * The save cube's week window (its ``CubeVersion``'s
   * ``year_week_min..year_week_max``). Cells outside it are read-only and
   * dimmed: the server rejects such saves. Leave undefined when no cube is
   * being written.
   */
  cubeWindow?: CubeWindow;
  /**
   * Grid-state retention handle from ``useGridStateRetention``: keeps the
   * user's column layout, hand-set filters and unsaved edits alive across
   * calendar-family switches and unmount/remount. Omit it for a transient
   * grid (the range-plan item modal), which gets plain per-mount state.
   */
  gridStateRetention?: GridStateRetention;
  /**
   * Column layout the applied view is waiting to replay, drained here once
   * the grid holds the columns it names. Omit it for a grid with no view
   * layer.
   */
  pendingViewColumnState?: ColumnState[] | null;
  /** Called once ``pendingViewColumnState`` has landed, to clear it. */
  onViewColumnStateApplied?: () => void;
  /** Forecast metadata for column definitions and metric filtering. */
  metaData: ForecastMetaDataType | undefined;
  /** Raises AG Grid's loading overlay. Which query state to pass is the caller's. */
  isMetaDataFetching: boolean;
}

export const ForecastDataTable = React.memo(
  React.forwardRef<AgGridReact, ForecastDataTableProps>(
    (
      {
        tableControls,
        viewState = NO_VIEW_STATE,
        editableProducer,
        editableRowPredicate,
        editableMetricsOverride,
        editableTimeWindowPredicate,
        headerTooltipYearWeekResolver,
        cubeWindow,
        gridStateRetention,
        pendingViewColumnState,
        onViewColumnStateApplied,
        metaData,
        isMetaDataFetching,
        rowData,
        onRowDataUpdated,
        onCellValueChanged,
        gridOptions,
        ...props
      },
      ref
    ) => {
      const {
        calendarView,
        weekLabels,
        timePeriod,
        planBy,
        planningYear,
        gridStyleGrouping,
        intake
      } = tableControls;
      const gridApiRef = useRef<GridApi | null>(null);
      const [initialState] = useState(() =>
        gridStateRetention?.initialState(
          calendarView,
          viewState,
          pendingViewColumnState
        )
      );

      const columnDefsMemoized = useMemo(
        () =>
          metaData
            ? createColumnDefinitions(
                TIME_PERIOD_TO_COLUMN_TYPE[timePeriod],
                CALENDAR_VIEW_TO_TYPE[calendarView],
                {
                  planBy,
                  planningYear,
                  metaData,
                  weekLabels,
                  intake,
                  editableProducer,
                  editableRowPredicate,
                  editableMetricsOverride,
                  editableTimeWindowPredicate,
                  headerTooltipYearWeekResolver,
                  cubeWindow
                }
              )
            : [],
        [
          calendarView,
          weekLabels,
          timePeriod,
          planBy,
          planningYear,
          metaData,
          intake,
          editableProducer,
          editableRowPredicate,
          editableMetricsOverride,
          editableTimeWindowPredicate,
          headerTooltipYearWeekResolver,
          cubeWindow
        ]
      );

      // The view's column layout waits for the columns it names: the grid
      // may not have mounted when the view was applied, and a period or
      // calendar switch rebuilds the defs.
      const drainPendingViewColumnState = useCallback(
        (api: GridApi) => {
          if (!pendingViewColumnState) return;
          const lands = pendingViewColumnState.some(
            (state) => state.colId && api.getColumn(state.colId)
          );
          if (!lands) return;
          api.applyColumnState({
            state: pendingViewColumnState,
            applyOrder: true
          });
          onViewColumnStateApplied?.();
        },
        [pendingViewColumnState, onViewColumnStateApplied]
      );

      // True while the view was last applied to a grid without rows. AG Grid
      // holds a filter model set before the first rows and applies it as
      // they start loading, when a set filter reading its keys from the
      // rows (every set filter but the metric column's) may know none and clear itself as "everything selected". So the
      // view lands again once the rows are in.
      const viewAwaitsRowsRef = useRef(false);

      // Which apply the grid last landed (``ResolvedViewState.applyId``).
      const landedApplyIdRef = useRef<number | undefined>(undefined);

      // The applied view owns the filter model and the sort together, so
      // both land in one pass. ``replace`` lands the whole model: an apply,
      // and the re-land once the rows are in. A re-resolve of the same view
      // re-lands only the filters it owns, so a column filter the planner
      // set by hand survives the calendar switch that triggered it; the
      // view's sort re-lands whole, since the switch renames its column.
      const applyViewState = useCallback(
        (api: GridApi, replace: boolean) => {
          landedApplyIdRef.current = viewState.applyId;
          if (replace) {
            api.setFilterModel(viewState.filterModel);
          } else {
            const model = {
              ...partitionViewOwnedFilters(api.getFilterModel()).planner,
              ...partitionViewOwnedFilters(viewState.filterModel).viewOwned
            };
            api.setFilterModel(Object.keys(model).length ? model : null);
          }
          if (replace || viewState.sortModel.length) {
            api.applyColumnState({
              state: viewState.sortModel,
              defaultState: { sort: null }
            });
          }
          viewAwaitsRowsRef.current = !api.getGridOption("rowData")?.length;
        },
        [viewState]
      );

      const handleRowDataUpdated = useCallback(
        (event: RowDataUpdatedEvent) => {
          if (
            viewAwaitsRowsRef.current &&
            event.api.getGridOption("rowData")?.length
          ) {
            applyViewState(event.api, true);
          }
          onRowDataUpdated?.(event);
        },
        [applyViewState, onRowDataUpdated]
      );

      // AG Grid binds asynchronously, so the effects below may already have
      // run against a null api. The view's filters land first, then the
      // retained layout and hand-set filters of a grid coming back from
      // navigation. A view layout still waiting to replay lands last: the
      // planner chose it after the stash was taken.
      const landOnReady = useCallback(
        (api: GridApi) => {
          applyViewState(api, true);
          gridStateRetention?.onGridReady(api, calendarView);
          drainPendingViewColumnState(api);
        },
        [
          applyViewState,
          drainPendingViewColumnState,
          gridStateRetention,
          calendarView
        ]
      );

      // True while the grid became ready without columns: it mounts before
      // the metadata when a retained load comes back after the query cache
      // dropped the metadata. Nothing lands on a columns-less grid, so the
      // ready work waits for the columns effect below — and until then the
      // grid's state is not captured, or the default layout the columns
      // arrive with would replace the stash.
      const readyAwaitsColumnsRef = useRef(false);

      const handleGridReady = useCallback(
        (params: { api: GridApi }) => {
          gridApiRef.current = params.api;
          if (columnDefsMemoized.length === 0) {
            readyAwaitsColumnsRef.current = true;
            return;
          }
          if (initialState) {
            landedApplyIdRef.current = viewState.applyId;
            viewAwaitsRowsRef.current =
              !params.api.getGridOption("rowData")?.length;
            if (pendingViewColumnState) onViewColumnStateApplied?.();
          } else {
            landOnReady(params.api);
          }
        },
        [
          landOnReady,
          columnDefsMemoized,
          initialState,
          viewState.applyId,
          pendingViewColumnState,
          onViewColumnStateApplied
        ]
      );

      const handleStateUpdated = useCallback(() => {
        if (!readyAwaitsColumnsRef.current) gridStateRetention?.capture();
      }, [gridStateRetention]);

      // On an index cell AG Grid reports value/oldValue in index points (in
      // variance points under "+/- Var"), but every consumer downstream
      // treats them as the metric's absolute
      // (the local recompute and the cube write both do). Hand them the
      // absolute the value setter already put on the row.
      const handleCellValueChanged = useCallback(
        (event: CellValueChangedEvent) => {
          if (!onCellValueChanged) return;
          onCellValueChanged(
            metaData
              ? {
                  ...event,
                  ...toAbsoluteCellChange(event, planBy, planningYear, metaData)
                }
              : event
          );
        },
        [onCellValueChanged, planBy, planningYear, metaData]
      );

      const getRowId = useCallback(
        (params: GetRowIdParams) => forecastRowId(params.data),
        []
      );

      useEffect(() => {
        const api = gridApiRef.current;
        if (!api) return;
        const reResolve =
          viewState.applyId !== undefined &&
          viewState.applyId === landedApplyIdRef.current;
        applyViewState(api, !reResolve);
      }, [applyViewState, viewState.applyId]);

      // Declared after the view effect so the view's year filter and sort
      // stand under the restored stash.
      useEffect(() => {
        gridStateRetention?.trackCalendarView(calendarView);
      }, [gridStateRetention, calendarView]);

      useEffect(() => {
        if (!gridApiRef.current) return;
        // Force redraw of rows when gridStyleGrouping changes to apply new row styles
        gridApiRef.current.redrawRows();
      }, [gridStyleGrouping]);

      useEffect(() => {
        const api = gridApiRef.current;
        if (!api || columnDefsMemoized.length === 0) return;
        if (readyAwaitsColumnsRef.current) {
          readyAwaitsColumnsRef.current = false;
          landOnReady(api);
        } else {
          drainPendingViewColumnState(api);
        }
        // ``columnDefsMemoized`` is the retry trigger: the columns a view
        // names appear when the defs are rebuilt.
      }, [landOnReady, drainPendingViewColumnState, columnDefsMemoized]);

      const rowStyleMemoized = useMemo(
        () =>
          metaData
            ? (params: RowClassParams) =>
                getRowStyle(gridStyleGrouping, params, metaData)
            : undefined,
        [gridStyleGrouping, metaData]
      );

      return (
        <DataTable
          ref={ref}
          initialState={initialState}
          columnDefs={columnDefsMemoized}
          rowData={rowData}
          selectionColumnDef={{
            width: 40,
            sortable: false,
            resizable: false,
            suppressHeaderMenuButton: true,
            pinned: "left"
          }}
          rowSelection={multiRowSelectionCheckboxOnly}
          columnHoverHighlight={true}
          rowHeight={40}
          statusBar={defaultSidebar.statusBar}
          alwaysMultiSort={true}
          // Commit an open editor on blur, so Apply and Save see the typed value.
          stopEditingWhenCellsLoseFocus={true}
          getRowStyle={rowStyleMemoized}
          getRowId={getRowId}
          loading={isMetaDataFetching}
          onGridReady={handleGridReady}
          onStateUpdated={handleStateUpdated}
          onCellValueChanged={handleCellValueChanged}
          onRowDataUpdated={handleRowDataUpdated}
          gridOptions={gridOptions}
          {...props}
        />
      );
    }
  )
);
